import {
  CreateDataRoomShareableLinkPayload,
  DataRoomShareableLink,
  ShareableLinkAnalyticsResponse,
  ShareDataRoomNodes,
  ShareableLinkAnalyticsFileEntry,
  ShareableLinkAnalyticsMember,
} from 'models/ShareableLink';
import httpClient from '../httpClient';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';

function createShareableLink(
  dataRoomId: string,
  data: CreateDataRoomShareableLinkPayload & {
    // Set when the link is created to answer a waiting room request, which the
    // BE clears off the queue once the link exists.
    waitingRoomRequestId?: string;
  },
) {
  return httpClient.post<{ dataRoomShareableLink: DataRoomShareableLink }>(
    `shareable-links/data-rooms/${dataRoomId}`,
    data,
  );
}

function getShareableLinks(
  dataRoomId: string,
  params: PaginationParams & { isActive?: boolean },
) {
  return httpClient.get<PaginatedData<DataRoomShareableLink>>(
    `shareable-links/data-rooms/${dataRoomId}`,
    { params },
  );
}

function changeShareableLinkState(shareableLinkId: string, isActive: boolean) {
  return httpClient.post<DataRoomShareableLink>(
    `shareable-links/${shareableLinkId}/change-state`,
    {
      isActive,
    },
  );
}

function patchShareableLink(
  shareableLinkId: string,
  data: Partial<CreateDataRoomShareableLinkPayload>,
) {
  return httpClient.patch<DataRoomShareableLink>(
    `shareable-links/${shareableLinkId}`,
    data,
  );
}

function grantAccess(requestId: string) {
  return httpClient.post(`shareable-link-requests/${requestId}/approve-access`);
}

function rejectAccess(requestId: string) {
  return httpClient.post(`shareable-link-requests/${requestId}/reject-access`);
}

function ignore(requestId: string) {
  return httpClient.post(`shareable-link-requests/${requestId}/ignore`);
}

function shareAllFiles(shareableLinkId: string, payload: ShareDataRoomNodes) {
  return httpClient.post(
    `shareable-links/${shareableLinkId}/share-all-files`,
    payload,
  );
}

function undoShareAllFiles(shareableLinkId: string) {
  return httpClient.post<ShareDataRoomNodes>(
    `shareable-links/${shareableLinkId}/undo-share-all-files`,
  );
}

function getAnalytics(shareableLinkId: string) {
  return httpClient.get<ShareableLinkAnalyticsResponse>(
    `shareable-links/${shareableLinkId}/analytics`,
  );
}

function getAnalyticsMembers(
  shareableLinkId: string,
  params: PaginationParams,
) {
  return httpClient.get<PaginatedData<ShareableLinkAnalyticsMember>>(
    `shareable-links/${shareableLinkId}/analytics/members`,
    { params },
  );
}

function getAnalyticsMemberFiles(
  shareableLinkId: string,
  memberId: string,
  params: PaginationParams,
) {
  return httpClient.get<PaginatedData<ShareableLinkAnalyticsFileEntry>>(
    `shareable-links/${shareableLinkId}/analytics/members/${memberId}`,
    { params },
  );
}

export default {
  createShareableLink,
  getShareableLinks,
  changeShareableLinkState,
  patchShareableLink,
  grantAccess,
  rejectAccess,
  ignore,
  shareAllFiles,
  undoShareAllFiles,
  getAnalytics,
  getAnalyticsMembers,
  getAnalyticsMemberFiles,
};
